import { ModuleCard } from '@/components/ModuleCard/ModuleCard';
import { EProgressStatus } from '@/models/enums';
import { PublicActivity } from '@/hooks/useGetPublicExercises/useGetPublicExercises';
import Link from 'next/link';
import PresignedUrlImage from '@/components/Image/PresignedUrlImage';
import { cn } from '@/utils';
import { isComingSoon as isTaggedComingSoon } from '@/utils/tags';

function isComingSoon(exercise: PublicActivity) {
  return isTaggedComingSoon(exercise.tags);
}

type ExerciseGroupProps = {
  title: string;
  exercises: PublicActivity[];
  viewMode?: 'cards' | 'list';
};

const TAG_DESCRIPTIONS: Record<string, string> = {
  'In the moment': 'Quick activities for when you are feeling stressed, worried or anxious. Most take under five minutes.',
  'Reflect and explore': 'Activities to help you pause, reflect and notice what’s going on for you.',
  'Plan and take action': 'Useful activities for planning and thinking through next steps.',
  'Get support': 'A list of services and resources for when you need to reach out for more help.',
};

function FeaturedExercise({ exercise }: { exercise: PublicActivity }) {
  const { url, title, description, estimatedTime, iconAsset } = exercise;
  const comingSoon = isComingSoon(exercise);

  const cardClassNames = cn(
    'flex rounded border border-gray-200 overflow-hidden bg-secondary no-underline mb-6 transition-shadow',
    comingSoon ? 'opacity-60 cursor-default' : 'hover:shadow-md',
  );

  const cardContent = (
    <>
      <div className="hidden sm:flex w-48 md:w-72 flex-shrink-0 bg-surface items-center justify-center min-h-[180px] relative">
        {comingSoon ? (
          <img src="/images/coming-soon-icon.svg" alt="Coming soon" width={160} height={160} className="object-contain" />
        ) : iconAsset?.presignedUrl ? (
          <PresignedUrlImage
            imageId={iconAsset.id}
            src={iconAsset.presignedUrl}
            alt={`${title} icon`}
            width={120}
            height={120}
            className="object-contain"
          />
        ) : (
          <span className="text-xs text-gray-400 text-center px-4">featured</span>
        )}
      </div>
      <div className="flex-1 p-5 md:p-8 flex flex-col gap-3">
        {comingSoon && (
          <span className="self-start px-3 py-1 rounded-full text-xs font-medium bg-surface text-surface-foreground border border-gray-200">
            Coming soon
          </span>
        )}
        <h3 className="text-xl md:text-2xl font-bold m-0 -mb-3 text-secondary-foreground">{title ?? 'No Title'}</h3>
        {estimatedTime && (
          <p className="text-sm text-gray-500 m-0">{estimatedTime}</p>
        )}
        {description && (
          <p className="text-secondary-foreground m-0 text-sm md:text-base">{description}</p>
        )}
        {!comingSoon && (
          <div className="mt-2">
            <span className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium">
              Begin &rarr;
            </span>
          </div>
        )}
      </div>
    </>
  );

  return comingSoon ? (
    <div className={cardClassNames}>{cardContent}</div>
  ) : (
    <Link href={`/exercise/${url}`} className={cardClassNames}>
      {cardContent}
    </Link>
  );
}

function ExerciseListItem({ exercise }: { exercise: PublicActivity }) {
  const { url, title, description, estimatedTime, iconAsset } = exercise;
  const comingSoon = isComingSoon(exercise);

  const rowClassNames = cn(
    'flex items-center gap-4 p-4 rounded border border-gray-200 bg-secondary no-underline transition-shadow',
    comingSoon ? 'opacity-60 cursor-default' : 'hover:shadow-sm',
  );

  const rowContent = (
    <>
      <div className="w-12 h-12 bg-surface rounded flex items-center justify-center flex-shrink-0 border border-gray-100">
        {comingSoon ? (
          <img src="/images/coming-soon-icon.svg" alt="Coming soon" width={44} height={44} className="object-contain" />
        ) : iconAsset?.presignedUrl ? (
          <PresignedUrlImage
            imageId={iconAsset.id}
            src={iconAsset.presignedUrl}
            alt={`${title} icon`}
            width={36}
            height={36}
            className="object-contain"
          />
        ) : (
          <span className="text-gray-400 text-lg">+</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold m-0 text-secondary-foreground">{title ?? 'No Title'}</p>
        {description && (
          <p className="text-sm text-gray-500 m-0 truncate">{description}</p>
        )}
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        {comingSoon ? (
          <span className="text-sm font-medium text-gray-500">Coming soon</span>
        ) : (
          <>
            {estimatedTime && (
              <span className="text-sm text-gray-500 hidden sm:block">{estimatedTime}</span>
            )}
            <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-gray-300 text-sm font-medium text-secondary-foreground">
              Begin &rarr;
            </span>
          </>
        )}
      </div>
    </>
  );

  return (
    <li className="m-0">
      {comingSoon ? (
        <div className={rowClassNames}>{rowContent}</div>
      ) : (
        <Link href={`/exercise/${url}`} className={rowClassNames}>
          {rowContent}
        </Link>
      )}
    </li>
  );
}

export const ExerciseGroup = ({ title, exercises, viewMode = 'cards' }: ExerciseGroupProps) => {
  const description = TAG_DESCRIPTIONS[title];

  if (viewMode === 'list') {
    const [featured, ...rest] = exercises;

    return (
      <section className="mb-10">
        <div className="bg-primary text-primary-foreground flex flex-col items-start px-4 py-3 rounded-t rounded-b mb-4">
          <h2 className="h5 m-0">{title}</h2>
          {description && <p className="text-sm mt-1 m-0">{description}</p>}
        </div>

        {featured && <FeaturedExercise exercise={featured} />}

        {rest.length > 0 && (
          <>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Also in this group</h3>
            <ul className="list-style-none m-0 flex flex-col gap-3">
              {rest.map((exercise) => (
                <ExerciseListItem key={exercise.id} exercise={exercise} />
              ))}
            </ul>
          </>
        )}
      </section>
    );
  }

  return (
    <section className="mb-8">
      <div className="bg-primary text-primary-foreground flex flex-col items-start px-4 py-3 rounded-t rounded-b mb-4">
        <h2 className="h5 m-0">{title}</h2>
        {description && <p className="text-sm mt-1 m-0">{description}</p>}
      </div>
      <div className="bg-secondary rounded p-4">
        <ul className="flex flex-wrap -mx-2 list-style-none m-0">
          {exercises.map((exercise) => {
            const { id, url, title: exerciseTitle, iconAsset } = exercise;
            return (
              <li key={id} className="w-full md:w-1/2 lg:w-1/4 p-2 m-0">
                <ModuleCard
                  type="ExerciseToolbox"
                  isLoading={false}
                  href={`/exercise/${url}`}
                  status={isComingSoon(exercise) ? EProgressStatus.ComingSoon : EProgressStatus.NotStarted}
                  title={exerciseTitle ?? 'No Title'}
                  iconUrl={iconAsset?.presignedUrl || ''}
                  imageId={iconAsset?.id!}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
